import { Box, useTheme } from '@mui/system';
import { SUPPORT } from 'business/constants';
import CreateDashboardModal from 'components/DashboardAddForm/CreateDashboardModal';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import { useIsEssential, useIsExploreUser, useUserHasRole } from 'hooks/client';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AddButton from 'ui/Buttons/AddButton';
import ArchivedDashboardsModal from '../ArchivedDashboards/ArchivedDashboardsModal';
import {
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';

type Props = {
    setSearchValue: (value: string) => void;
    showArchivedDashboardsButton?: boolean;
    showToggleDisplayButton?: boolean;
    viewDisplay?: string;
    setViewDisplay?: (value: string) => void;
};

const DashboardsTabHeader = ({
    setSearchValue,
    showArchivedDashboardsButton = false,
    showToggleDisplayButton = false,
    viewDisplay = undefined,
    setViewDisplay = undefined,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [showNewDashboardModal, setShowNewDashboardModal] = useState(false);
    const [showArchivedDashboardsModal, setShowArchivedDashboardsModal] =
        useState(false);

    const isExploreUser = useIsExploreUser();
    const hasEssential = useIsEssential();
    const isSupportUser = useUserHasRole(SUPPORT);

    const createNewDashboard = useCallback(
        () => setShowNewDashboardModal(true),
        [],
    );

    const canAddForExploreUser = !hasEssential && isExploreUser;
    const canAddForEssentialClient = hasEssential && isSupportUser;
    const newDashboard = () =>
        canAddForExploreUser || canAddForEssentialClient ? (
            <AddButton
                text={t('dashboards.newDashboard')}
                onClick={createNewDashboard}
                data-testid="create-dashboard-button"
            />
        ) : (
            <></>
        );

    const newDashboardButton = useMemo(() => newDashboard(), []);

    const handleDisplay = (
        _event: React.MouseEvent<HTMLElement>,
        newDisplay: string,
    ) => {
        if (setViewDisplay && newDisplay) {
            setViewDisplay(newDisplay);
            localStorage.setItem('dashboardsDisplay', newDisplay);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    my: theme.spacing(3),
                    mx: theme.spacing(2),
                }}
            >
                <SearchBar
                    width="lg"
                    onChange={(value) => setSearchValue(value)}
                    placeholder={t('dashboards.search')}
                />

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    {showArchivedDashboardsButton && (
                        <Button
                            variant="outlined"
                            startIcon={<ArchiveOutlinedIcon />}
                            onClick={() => setShowArchivedDashboardsModal(true)}
                        >
                            <Typography variant="body1SemiBold" color="inherit">
                                {t('dashboards.archivedDashboardsButton')}
                            </Typography>
                        </Button>
                    )}
                    {showToggleDisplayButton && (
                        <ToggleButtonGroup
                            value={viewDisplay}
                            onChange={handleDisplay}
                            size="small"
                            exclusive
                        >
                            <ToggleButton value="grid" data-testid="grid-view">
                                <ViewModuleIcon />
                            </ToggleButton>
                            <ToggleButton
                                value="table"
                                data-testid="table-view"
                            >
                                <TableRowsIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {newDashboardButton}
                </Box>
            </Box>
            {showNewDashboardModal && (
                <CreateDashboardModal
                    onCreate={(dashboard) =>
                        navigate(`/dashboard/${dashboard.slug}`)
                    }
                    isOpen={showNewDashboardModal}
                    onClose={() => setShowNewDashboardModal(false)}
                />
            )}
            {showArchivedDashboardsModal && (
                <ArchivedDashboardsModal
                    isOpen={showArchivedDashboardsModal}
                    onClose={() => setShowArchivedDashboardsModal(false)}
                />
            )}
        </>
    );
};

export default DashboardsTabHeader;
